import React, { useCallback, useMemo, useState } from 'react';
import { useMutation, useQuery } from 'react-query';
import { useNavigate } from 'react-router-dom';

import { FormInfoTourney } from './components';

import { Button, Loader, Modal } from '@/components';
import { Layout, TourneyList } from '@/components/GenericsTournaments';
import useAuth from '@/hooks/useAuth';
import { useIsDesktop } from '@/hooks/useIsDesktop';
import useToast from '@/hooks/useToast';
import useTourney from '@/hooks/useTourney';
import ROUTES from '@/router/routes';
import { inviteUserById } from '@/services/invites';
import {
  ActionTypeManageTDAEnum,
  getAdminTda,
  postManageAllPostulant,
  postManagePostulant,
  requestExpelMember,
} from '@/services/tda';
import {
  ActionsTourneys,
  AdminItemInvitation,
  AdminItemParticipando,
  GenericItemTourneysData,
} from '@/types/tda';
import { EnumContextTourneys } from '@/types/team';
import { TEXTS } from '@/utils';
import { SECTION_NAME_FOR_ADS } from '@/utils/ads';

const initialState = {
  showModalRequest: false,
  showModalExpel: false,
  nameUser: '',
  idUser: 0,
};

export const ViewAdminTda: React.FC = React.memo(() => {
  const navigate = useNavigate();
  const toast = useToast();
  const { tourneyData } = useTourney();
  const { userInitialData } = useAuth();
  const { isMobile, isDesktop } = useIsDesktop();
  const [state, setState] = useState(initialState);

  const { isLoading, data, isSuccess, refetch, isRefetching } = useQuery(
    'initViewAdmin',
    async () => {
      return await getAdminTda(tourneyData.id);
    },
    {
      refetchOnWindowFocus: false,
      keepPreviousData: false,
    }
  );

  const { mutateAsync: sendInvitation, isLoading: loadingInvitation } =
    useMutation(
      async (userId: number) => await inviteUserById(tourneyData.id, userId),
      {
        onSuccess: (response) => {
          if ('estado' in response && response.estado) {
            toast.show('La invitación fue enviada exitosamente.');
            refetch();
          } else if (
            'invPendiente' in response &&
            response.invPendiente &&
            response?.idUsuarioDT
          ) {
            // acepto al usuario postulado automaticamente
            onHandleRequestActions(
              ActionTypeManageTDAEnum.Accept,
              response?.idUsuarioDT
            );
          } else {
            toast.show(
              response.mensaje ?? 'Hubo un problema al enviar la invitación.'
            );
          }
        },
        onError: () => {
          toast.show(
            'Ocurrió un error al enviar la invitación. Por favor, intenta de nuevo.'
          );
        },
      }
    );

  const memberList = useMemo((): GenericItemTourneysData[] | null => {
    if (!data?.estado || data.pagePart.objectList.length === 0) return null;
    return (
      data.pagePart.objectList.map((item: AdminItemParticipando) => {
        return {
          id: item.idUsuario,
          title: item.nombreYApellido,
          action: {
            txtAction: 'Expulsar',
            variantButton: 'danger',
            txtActionDisabled: 'Expulsar',
          },
          profilePicUrl: item.fotoPerfil,
          profileName: item.nombreYApellido,
          disabled: userInitialData?.idUsuarioDT === item.idUsuario,
          subtitle: isMobile
            ? item.nombreEquipo.toLocaleUpperCase()
            : item.nombreEquipo,
        };
      }) || []
    );
  }, [data, isMobile, userInitialData]);

  const invitationList = useMemo((): GenericItemTourneysData[] | null => {
    if (!data?.estado || data.pageInvitados.objectList.length === 0)
      return null;
    const list = data.pageInvitados.objectList;
    return list.map((item: AdminItemInvitation) => {
      return {
        id: item.idUsuario,
        action: {
          txtAction: 'Reenviar',
          txtActionDisabled: 'Reenviar',
        },
        profilePicUrl: item.fotoPerfil,
        profileName: item.nombreYApellido,
        title: item.nombreYApellido,
        subtitle: item.nombreEquipo,
        disabled: item?.idEstado === 4,
      };
    });
  }, [data]);

  const requestRecivedList = useMemo((): GenericItemTourneysData[] | null => {
    if (!data?.estado || data.pageAcep.objectList.length === 0) return null;
    return (
      data.pageAcep.objectList.map((item: AdminItemParticipando) => {
        return {
          id: item.idUsuario,
          title: item.nombreYApellido,
          profilePicUrl: item.fotoPerfil,
          profileName: item.nombreYApellido,
          subtitle: isMobile
            ? item.nombreEquipo.toLocaleUpperCase()
            : item.nombreEquipo,
        };
      }) || []
    );
  }, [data, isMobile]);

  // VER MODAL EXPULSAR MIEMBRO
  const onHandleMembers = useCallback(
    async (action: ActionsTourneys) => {
      const dataUser = memberList?.find((user) => user.id === action.id);
      setState({
        showModalExpel: true,
        showModalRequest: false,
        nameUser: `${dataUser?.title}`,
        idUser: dataUser?.id || 0,
      });
    },
    [memberList]
  );

  // TODO: EXPULSAR MIEMBROS TORNEO
  const onHandleExpelMembers = async (idUser: number) => {
    try {
      const responseExpel = await requestExpelMember({
        idTourney: tourneyData.id,
        idUser,
      });

      if (!responseExpel.estado) {
        console.error(responseExpel.mensaje);
        toast.show(TEXTS.defaultError);
      }
    } catch (error) {
      console.error(error);
      toast.show(TEXTS.defaultError);
    }

    setState(initialState);
    refetch();
  };

  //  TRATAR ACEPTAR/RECHAZAR SOLICITUDES RECIBIDAS
  const onHandleRequestActions = useCallback(
    async (actionType: ActionTypeManageTDAEnum, idUser: number) => {
      try {
        const responseManagePostulant = await postManagePostulant({
          actionType,
          idTourney: tourneyData.id,
          idUser,
        });

        if (responseManagePostulant.estado) {
          // eslint-disable-next-line no-console
          console.log(
            responseManagePostulant.estado,
            'responseManagePostulant'
          );
        } else {
          console.error(responseManagePostulant.mensaje);
          toast.show(TEXTS.defaultError);
        }
      } catch (error) {
        console.error(error);
        toast.show(TEXTS.defaultError);
      }

      setState(initialState);
      refetch();
    },
    [refetch, toast, tourneyData.id]
  );

  // VER MODAL SOLICITUD RECIBIDA
  const onHandleRequestRecived = async (params: ActionsTourneys) => {
    const dataUser = requestRecivedList?.find((user) => user.id === params.id);

    if (!dataUser?.id) return;

    if (isMobile) {
      setState({
        showModalExpel: false,
        showModalRequest: true,
        nameUser: `${dataUser?.title}`,
        idUser: dataUser?.id || 0,
      });
    } else {
      if (params.action === 'reject') {
        onHandleRequestActions(ActionTypeManageTDAEnum.Reject, dataUser.id);
      } else {
        onHandleRequestActions(ActionTypeManageTDAEnum.Accept, dataUser.id);
      }
    }
  };

  const handleManageAllPostulant = useCallback(async () => {
    try {
      const responseManagePostulant = await postManageAllPostulant(
        tourneyData.id
      );

      if (responseManagePostulant.estado) {
        // eslint-disable-next-line no-console
        console.log(responseManagePostulant.estado, 'responseManagePostulant');
      } else {
        console.error(responseManagePostulant.mensaje);
        toast.show(TEXTS.defaultError);
      }
    } catch (error) {
      console.error(error);
      toast.show(TEXTS.defaultError);
    }

    setState(initialState);
    refetch();
  }, [refetch, toast, tourneyData.id]);

  const RequestRecivedListUI = useCallback(() => {
    return (
      requestRecivedList && (
        <TourneyList
          action={{
            txtAction: 'Ver solicitud',
            variantButton: 'secondary',
          }}
          className="lg:mt-9 mt-6"
          classPaginator="paginator-sidebar"
          context={EnumContextTourneys.Sidebar}
          handleSubmit={(params) => {
            if (isMobile) {
              params.id && onHandleRequestRecived(params);
            } else {
              params.action && onHandleRequestRecived(params);
            }
          }}
          headerAction={
            <div className="flex justify-around items-center mb-3">
              <Button
                className="text-[14px] text-gdt-blue font-bold"
                href="#"
                type="link"
                variant="text"
                onClick={handleManageAllPostulant}
              >
                Aceptar todas
              </Button>
            </div>
          }
          list={requestRecivedList}
          paginator={isDesktop}
          title={<p className="px-4 lg:px-0">Solicitudes</p>}
          variant={isMobile ? 'default' : 'two-actions'}
          withAvatar={true}
        />
      )
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMobile, requestRecivedList]);

  const ButtonInvitation = useCallback(() => {
    return (
      <div className="text-center lg:text-right">
        <Button
          className="font-semibold underline-offset-4 lg:mt-0 mt-6 lg:w-[203px]"
          type="button"
          variant={isMobile ? 'text' : 'secondary'}
          onClick={() => navigate(ROUTES.InviteToFriendsTournament)}
        >
          Invitar amigos
        </Button>
      </div>
    );
  }, [isMobile, navigate]);

  const adsConfig = useMemo(() => {
    return tourneyData.isVip
      ? SECTION_NAME_FOR_ADS.torneovip
      : SECTION_NAME_FOR_ADS.torneodeamigos;
  }, [tourneyData.isVip]);

  return (
    <>
      <Loader
        isVisible={
          isLoading || isRefetching || !isSuccess || !data || loadingInvitation
        }
      />
      <Layout
        adsSections={adsConfig}
        backButton={isDesktop && true}
        main={() =>
          data?.estado && (
            <>
              {isDesktop && (
                <div>
                  <h1 className="text-2xl text-black font-bold">
                    Administrar Torneo
                  </h1>
                  {/* // TODO: falta servicio de nombre del dueño */}
                  <p className="text-sm text-gdt-gray-4 font-semibold mt-4 mb-">
                    Admin: {userInitialData?.nombre_dt} -{' '}
                    {data.pagePart.objectList.length !== 0 &&
                      (data.pagePart.objectList.length !== 1
                        ? data.pagePart.objectList.length + ' PARTICIPANTES'
                        : data.pagePart.objectList.length + ' PARTICIPANTE')}
                  </p>
                </div>
              )}
              {/* FORMULARIO DE TORNEO */}
              <div className="lg:max-w-[332px]">
                <FormInfoTourney
                  tourneyDataProps={data}
                  tourneyIdProps={tourneyData.id}
                  onSubmit={refetch}
                />
              </div>

              <div className="lg:max-w-[556px]">
                {/* SOLICITUDES */}
                {isMobile && <RequestRecivedListUI />}

                {isMobile && <ButtonInvitation />}

                {/* MIEMBROS TORNEO */}
                {memberList && (
                  <TourneyList
                    key={`${memberList.length}-members-list`}
                    className="lg:mt-9 mt-6"
                    context={EnumContextTourneys.Admin}
                    handleSubmit={(action) => {
                      action.id && onHandleMembers(action);
                    }}
                    list={memberList}
                    paginator={isDesktop}
                    title={<p className="px-4 lg:px-0">Miembros</p>}
                    variant="default"
                    withAvatar={true}
                  />
                )}

                {/* DT's Invitados */}
                {invitationList && (
                  <TourneyList
                    key={`${invitationList.length}-invitation-list`}
                    className="lg:mt-9 mt-6"
                    context={EnumContextTourneys.Admin}
                    handleSubmit={(action) => {
                      action.id && sendInvitation(action.id);
                    }}
                    list={invitationList}
                    paginator={isDesktop}
                    title={<p className="px-4 lg:px-0">DT&apos;s invitados</p>}
                    variant="default"
                    withAvatar={true}
                  />
                )}
              </div>
              {/* MODAL ACEPTACION ACEPTACION/RECHAZO INVITACION RECIBIDAS */}

              <Modal
                controlledOnClose={() =>
                  setState({ ...state, showModalRequest: false })
                }
                isControlledOpen={state.showModalRequest}
                render={() => {
                  return (
                    <div className="text-center mt-2">
                      <p className="text-base font-medium">
                        <strong>{state.nameUser}</strong> quiere unirse a tu
                        <br />
                        Torneo de Amigos
                      </p>
                      <div className="flex space-x-2 my-10">
                        <Button
                          className="flex-1"
                          type="button"
                          variant="secondary"
                          onClick={() =>
                            onHandleRequestActions(
                              ActionTypeManageTDAEnum.Reject,
                              state.idUser
                            )
                          }
                        >
                          Rechazar
                        </Button>
                        <Button
                          className="flex-1"
                          type="button"
                          variant="primary"
                          onClick={() =>
                            onHandleRequestActions(
                              ActionTypeManageTDAEnum.Accept,
                              state.idUser
                            )
                          }
                        >
                          Aceptar
                        </Button>
                      </div>
                    </div>
                  );
                }}
                variant="controlled"
              />

              <Modal
                controlledOnClose={() =>
                  setState({ ...state, showModalExpel: false })
                }
                isControlledOpen={state.showModalExpel}
                render={() => {
                  return (
                    <div className="text-center mt-2">
                      <p className="text-base font-medium">
                        ¿Estás seguro que deseás EXPULSARLO?
                      </p>
                      <div className="flex space-x-2 my-10">
                        <Button
                          className="flex-1"
                          type="button"
                          variant="secondary"
                          onClick={() =>
                            setState({ ...state, showModalExpel: false })
                          }
                        >
                          Cancelar
                        </Button>
                        <Button
                          className="flex-1"
                          type="button"
                          variant="primary"
                          onClick={() => onHandleExpelMembers(state.idUser)}
                        >
                          Aceptar
                        </Button>
                      </div>
                    </div>
                  );
                }}
                size="xs"
                variant="controlled"
              />
            </>
          )
        }
        mobile={{
          leftContent: {
            backAction: () => {
              navigate(ROUTES.viewTourney);
            },
          },
          centerContent: {
            title: 'Administrar Torneo',
          },
        }}
        sidebar={() =>
          isDesktop && (
            <>
              {/* botton invitacion  */}
              <ButtonInvitation />
              {/* SOLICITUDES */}
              {<RequestRecivedListUI />}
            </>
          )
        }
        sidebarClassName="lg:w-[427px]"
        withBanners={isDesktop}
      />
    </>
  );
});

ViewAdminTda.displayName = 'ViewAdminTda';
